<template>
  <div id="app">
    <div id="loading">
      <b-spinner
        style="width: 3rem; height: 3rem"
        label="Carregando..."
      ></b-spinner>
    </div>
    <Header v-if="!$route.meta.hideNavigation" />
    <PendingNotificationsBanner
      v-if="showOverdueBanner"
      variant="danger"
      :count="pendingNotificationBanner.overdue"
      @view="goToNotificationsInbox"
      @dismiss="dismissPendingNotifications('overdue')"
    />
    <PendingNotificationsBanner
      v-if="showNearBanner"
      variant="warning"
      :count="pendingNotificationBanner.near"
      @view="goToNotificationsInbox"
      @dismiss="dismissPendingNotifications('near')"
    />
    <PendingNotificationsBanner
      v-if="showPendingBanner"
      variant="info"
      :count="pendingNotificationBanner.pending"
      @view="goToNotificationsInbox"
      @dismiss="dismissPendingNotifications('pending')"
    />
    <!-- Router -->
    <router-view />

    <button
      :class="`upToTop ${scrollpx > 350 ? 'active' : ''}`"
      @click="upPage"
    >
      <b-icon-arrow-up-circle-fill />
    </button>
    <MobileWarningModal />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import Header from "@/components/Header.vue";
import MobileWarningModal from "@/components/modals/MobileWarningModal.vue";
import PendingNotificationsBanner from "@/components/PendingNotificationsBanner.vue";
import { PENDING_NOTIFICATIONS_CACHE_TTL_MS } from "@/store/shared/actions";

export default {
  name: "App",
  components: {
    Header,
    MobileWarningModal,
    PendingNotificationsBanner,
  },

  data() {
    return {
      scrollpx: 0,
      pendingNotificationsPollId: null,
    };
  },

  computed: {
    ...mapGetters({
      currentUser: "auth/getAuthData",
      getLoginStatus: "auth/getLoginStatus",
      pendingNotificationBanner: "shared/pendingNotificationBanner",
    }),

    isTeacher() {
      return (
        this.currentUser &&
        this.currentUser.role &&
        this.currentUser.role.includes("teacher")
      );
    },

    showOverdueBanner() {
      return (
        this.isTeacher &&
        !this.$route.meta.hideNavigation &&
        this.$route.name !== "professor-notificacoes-inbox" &&
        this.pendingNotificationBanner &&
        !this.pendingNotificationBanner.dismissedOverdue &&
        this.pendingNotificationBanner.overdue > 0
      );
    },

    showNearBanner() {
      return (
        this.isTeacher &&
        !this.$route.meta.hideNavigation &&
        this.$route.name !== "professor-notificacoes-inbox" &&
        this.pendingNotificationBanner &&
        !this.pendingNotificationBanner.dismissedNear &&
        this.pendingNotificationBanner.near > 0
      );
    },

    showPendingBanner() {
      return (
        this.isTeacher &&
        !this.$route.meta.hideNavigation &&
        this.$route.name !== "professor-notificacoes-inbox" &&
        this.pendingNotificationBanner &&
        !this.pendingNotificationBanner.dismissedPending &&
        this.pendingNotificationBanner.pending > 0
      );
    },

    showAdminBoard() {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.includes("ROLE_ADMIN");
      }

      return false;
    },
    showModeratorBoard() {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.includes("ROLE_MODERATOR");
      }

      return false;
    },
  },

  methods: {
    ...mapMutations({
      setUserByToken: "auth/setUserByToken",
      dismissPendingNotifications: "shared/DISMISS_PENDING_NOTIFICATIONS",
    }),
    ...mapActions({
      fetchTeacherPendingNotifications: "shared/fetchTeacherPendingNotifications",
    }),

    goToNotificationsInbox() {
      this.$router.push({ name: "professor-notificacoes-inbox" });
    },

    upPage() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    handleScroll() {
      this.scrollpx = window.scrollY;
    },

    startPendingNotificationsPolling() {
      if (this.pendingNotificationsPollId) {
        return;
      }

      this.pendingNotificationsPollId = setInterval(() => {
        this.fetchTeacherPendingNotifications();
      }, PENDING_NOTIFICATIONS_CACHE_TTL_MS);
    },

    stopPendingNotificationsPolling() {
      if (this.pendingNotificationsPollId) {
        clearInterval(this.pendingNotificationsPollId);
        this.pendingNotificationsPollId = null;
      }
    },
  },

  watch: {
    isTeacher: {
      immediate: true,
      handler(value) {
        if (value) {
          this.fetchTeacherPendingNotifications();
          this.startPendingNotificationsPolling();
        } else {
          this.stopPendingNotificationsPolling();
        }
      },
    },

    "$route.path"() {
      if (this.isTeacher) {
        this.fetchTeacherPendingNotifications();
      }
    },
  },

  mounted() {
    if (
      process.env.NODE_ENV === "staging" ||
      process.env.NODE_ENV === "development"
    ) {
      console.log("Você está em um Ambiente de Testes");
    }

    let accessToken = "";
    const localUser = localStorage.getItem("user");

    if (localUser) accessToken = JSON.parse(localUser).access_token;

    if (accessToken) this.setUserByToken(accessToken);
  },

  created() {
    window.addEventListener("scroll", this.handleScroll);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
    this.stopPendingNotificationsPolling();
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
#app {
  padding: 0;
  margin: 0;
  height: 100%;
  width: 100%;
}

.upToTop {
  background: #fff;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50px;
  padding: 0;
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: -1;
  visibility: hidden;
  opacity: 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: 0.2s all ease-in-out;

  &.active {
    z-index: 200;
    visibility: visible;
    opacity: 1;
    transition: 0.2s all ease-in-out;
  }

  svg {
    font-size: 38px;
    color: #b23066;
  }
}
</style>
